import { useCallback, useMemo } from "react";
import { createDocumentResource, type DocumentResource } from "@/lib/document-resource";
import { openDocumentResource } from "@/stores/document-preview-store";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import {
  DeleteSharedItemDocument,
  RateSharedItemDocument,
  SharedItemDetailDocument,
  SharedItemFileContentsDocument,
  SignUpSharedProjectDocument,
  UpdateSharedItemDocument,
  type SharedItemDetailQuery,
} from "@/graphql/generated/operations";
import { base64ToBlob } from "@/lib/use-open-document";
import { downloadBlob } from "@/lib/file-download";
import { useCompanyContext } from "@/pages/company/use-company-context";
import { actingAsDeclaration } from "@/lib/share-item";

export type SharedItem = NonNullable<SharedItemDetailQuery["sharedItem"]>;

export interface SharedItemFormInput {
  name: string;
  shortDescription: string;
  description: string;
}

export interface UseSharedItemResult {
  attachments: Array<NonNullable<SharedItem["files"]>[number] & { document: DocumentResource }>;
  item: SharedItem | null;
  loading: boolean;
  notFound: boolean;
  error: Error | null;
  busy: boolean;
  update: (input: SharedItemFormInput) => Promise<void>;
  remove: () => Promise<void>;
  rate: (fiveStarRating: number) => Promise<void>;
  signUp: (signedUp: boolean) => Promise<void>;
  downloadFile: (fileId: string) => Promise<void>;
  openFile: (fileId: string) => Promise<void>;
  refetch: () => Promise<unknown>;
}

export function useSharedItem(itemId: string): UseSharedItemResult {
  const query = useQuery(SharedItemDetailDocument, {
    variables: { id: itemId },
    skip: !itemId,
  });
  const [updateMut, updateState] = useMutation(UpdateSharedItemDocument);
  const [deleteMut, deleteState] = useMutation(DeleteSharedItemDocument);
  const [rateMut, rateState] = useMutation(RateSharedItemDocument);
  const [signUpMut, signUpState] = useMutation(SignUpSharedProjectDocument);
  const apollo = useApolloClient();
  // THE DECLARATION EVERY WRITE ON A COMPANY'S ITEM HAS TO CARRY SINCE CONCEPT v23
  // (the company account model, Kevin 2026-09-11): editing or deleting a
  // company's post or project takes a current admin membership AND acting as that company, and
  // genesis refuses the write when the declaration is absent or names another company.
  //
  // READ HERE RATHER THAN PASSED IN, so no caller of this hook can forget it. The RESOLVED context is
  // the one that travels — `useCompanyContext` answers only for a membership row genesis returned
  // with `role = admin`, so a stored id with nothing behind it declares nothing.
  //
  // IT AUTHORIZES NOTHING. genesis re-checks the `company_membership` admin row exactly as before and
  // reads the governing company off the ROW; this only has to AGREE with it (genesis#300).
  const { active: actingAsCompany } = useCompanyContext();

  const item = query.data?.sharedItem ?? null;
  // Preserve the actual mode so genesis can reject mismatched ownership.
  const actingAs = actingAsDeclaration(actingAsCompany?.id ?? null);

  async function update(input: SharedItemFormInput) {
    await updateMut({
      variables: {
        id: itemId,
        input: {
          data: {
            name: input.name.trim(),
            shortDescription: input.shortDescription,
            description: input.description,
          },
          dryRun: false,
          ...actingAs,
        },
      },
    });
    await query.refetch();
  }

  async function remove() {
    await deleteMut({
      variables: { input: { id: itemId, ...actingAs } },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "SharedItem", id: itemId }) });
        // Force any active /share or /save list to re-resolve sharedItems
        // from the network — the entity eviction alone leaves a dangling
        // ref in the list nodes.
        cache.evict({ fieldName: "sharedItems" });
        cache.gc();
      },
    });
  }

  async function rate(fiveStarRating: number) {
    await rateMut({
      variables: { input: { sharedItemId: itemId, fiveStarRating } },
    });
    await query.refetch();
  }

  async function signUp(signedUp: boolean) {
    await signUpMut({
      variables: { input: { sharedItemId: itemId, signedUp } },
    });
    await query.refetch();
  }

  // Attachment bytes are not part of SharedItemDetail (that would pull every
  // file's Binary on each page view). Fetch them on demand.
  const fetchFileContent = useCallback(async (fileId: string) => {
    const { data } = await apollo.query({
      query: SharedItemFileContentsDocument,
      variables: { id: itemId },
      fetchPolicy: "no-cache",
    });
    const file = data?.sharedItem?.files?.find((f) => f?.id === fileId);
    if (!file?.content) {
      throw new Error("shared item attachment has no downloadable content");
    }
    return {
      content: file.content,
      mimeType: file.mimeType ?? "application/octet-stream",
      fileName: file.fileName,
      fileExtension: file.fileExtension,
    };
  }, [apollo, itemId]);

  const fileResources = useMemo(() => new Map((item?.files ?? []).map(file => [file.id, createDocumentResource({
    name: file.fileName ?? `document.${file.fileExtension?.replace(/^\./, "") ?? "bin"}`, mimeType: file.mimeType,
    load: async () => { const bytes = await fetchFileContent(file.id); return base64ToBlob(bytes.content, bytes.mimeType); },
  })])), [item?.files, fetchFileContent]);

  async function downloadFile(fileId: string) {
    const file = await fetchFileContent(fileId);
    const blob = base64ToBlob(file.content, file.mimeType);
    const ext = file.fileExtension?.replace(/^\./, "");
    downloadBlob(blob, file.fileName ?? (ext ? `download.${ext}` : "download"));
  }

  async function openFile(fileId: string) {
    const file = await fetchFileContent(fileId);
    await openDocumentResource(createDocumentResource({
      name: file.fileName ?? `document.${file.fileExtension?.replace(/^\./, "") ?? "bin"}`,
      mimeType: file.mimeType,
      load: async () => base64ToBlob(file.content, file.mimeType),
    }));
  }

  return {
    item,
    loading: query.loading && !query.data,
    notFound: !query.loading && !item,
    // THE READ'S ERROR ONLY. A failed MUTATION must not be reported here: the page branches on this
    // value and replaces the whole item with "Error: …", so a refused delete, rating or sign-up blew
    // the item off the screen and left it that way — nothing calls reset(). Concept v9 made that
    // reachable in ordinary use, because an INACTIVE company's items stay listed on its page and
    // refuse exactly those three actions.
    //
    // Every caller of remove/rate/signUp already receives the rejection and answers locally, which is
    // where a per-action failure belongs.
    error: (query.error as Error | undefined) ?? null,
    busy:
      updateState.loading ||
      deleteState.loading ||
      rateState.loading ||
      signUpState.loading,
    update,
    remove,
    rate,
    signUp,
    downloadFile,
    openFile,
    attachments: (item?.files ?? []).map(file => ({ ...file, document: fileResources.get(file.id)! })),
    refetch: () => query.refetch(),
  };
}
